import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, BookOpen } from "lucide-react";
import api from "@/lib/api";
import Seo, { buildBreadcrumbs } from "@/components/site/Seo";

export default function BlogIndex() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .get("/posts")
      .then((r) => setPosts(r.data))
      .catch(() => setError("Could not load posts."))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="blog-index">
      <Seo
        title="Insights & Cost Guides"
        description="Construction insights, cost guides and renovation advice from Prime Build Design LTD — premium London builders."
        path="/blog"
        jsonLd={buildBreadcrumbs([{ name: "Insights", path: "/blog" }])}
      />

      <section className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="overline mb-6">Insights & Guides</div>
          <h1 className="font-display text-5xl md:text-6xl font-bold text-slate-900 tracking-tight leading-[1.05] max-w-4xl">
            Field notes from<br /> our London sites.
          </h1>
          <p className="mt-6 text-slate-600 text-lg max-w-2xl leading-relaxed">
            Cost guides, planning explainers and the lessons learned from ten years building London homes.
          </p>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          {loading && (
            <div className="text-slate-500" data-testid="blog-loading">Loading…</div>
          )}
          {!loading && error && (
            <div className="text-red-700" data-testid="blog-error">{error}</div>
          )}
          {!loading && !error && posts.length === 0 && (
            <div className="border border-slate-200 p-12 text-center" data-testid="blog-empty">
              <BookOpen size={28} className="mx-auto text-slate-400" />
              <div className="mt-4 text-slate-600 max-w-lg mx-auto">
                We're publishing soon. In the meantime, browse our <Link className="underline" to="/projects">recent projects</Link> or
                request a <Link className="underline" to="/contact">free consultation</Link>.
              </div>
            </div>
          )}

          {!loading && !error && posts.length > 0 && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((p) => (
                <Link
                  key={p.id}
                  to={`/blog/${p.slug}`}
                  className="group bg-white border border-slate-200 hover:border-slate-400 transition-colors flex flex-col"
                  data-testid={`blog-card-${p.slug}`}
                >
                  {p.hero_image && (
                    <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                      <img
                        src={p.hero_image}
                        alt={p.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                  )}
                  <div className="p-6 flex flex-col flex-1">
                    {p.category && (
                      <div className="overline text-[#C5A059] mb-3">{p.category}</div>
                    )}
                    <h3 className="font-display text-2xl font-semibold text-slate-900 leading-tight">
                      {p.title}
                    </h3>
                    <p className="mt-3 text-sm text-slate-600 line-clamp-3 leading-relaxed flex-1">
                      {p.excerpt}
                    </p>
                    <div className="mt-6 flex items-center justify-between gap-4 text-[11px] tracking-[0.16em] uppercase text-slate-500">
                      <span>
                        {new Date(p.created_at).toLocaleDateString("en-GB", { month: "short", year: "numeric" })}
                      </span>
                      <span className="inline-flex items-center gap-1 text-slate-900 font-medium group-hover:text-[#C5A059] transition-colors">
                        Read <ArrowUpRight size={12} />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
